import React, { useEffect, useContext } from 'react';
import { Grid, Table, TableHeaderRow, TableColumnVisibility, TableColumnReordering, PagingPanel } from '@devexpress/dx-react-grid-bootstrap4';
import {SortingState, SelectionState, IntegratedSorting, PagingState, CustomPaging} from '@devexpress/dx-react-grid';
import moment from 'moment';
import axios from 'axios';

import ConvertTableToExcel from '../shared/ConvertTableToExcel';

import NumberOfSearchResults from '../shared/NumberOfSearchResults';
import { ChangeTablePage, HandlePageLoading } from '../shared/TableEventHelpers';
import TableFooter from '../shared/TableFooter';
import NoDataRow from '../shared/NoDataRow';
import TableRow from '../shared/TableRow';


import OfficeHoursTableHeader from './OfficeHoursTableHeader';
import OfficeHoursTableCell from './OfficeHoursTableCell';

import DatePickers from './DatePickers';
import FTECheckbox from './FTECheckbox';

import { DataContext } from '../../../stores/officehours-table-store';
import { DataContext as MainStoreContext } from '../../../store';
import Loading from '../../Loading';

import { useTranslation } from 'react-i18next';
import {PagingShowAllText} from "../../../functions/TableFunctions";


const CancelToken = axios.CancelToken;
let cancel;

const OfficeHoursTable = () => {
    const { store, dispatch } = useContext(DataContext);
    const mainStore = useContext(MainStoreContext);

    const {
        isLoading,
        tableSize,
        columnName,
        filters,
        direction,
        pageSize,
        error,
        offices,
        columns,
        hiddenColumns,
        currentPage,
        columnOrder,
        firstInvoiceMonth,
        integratedSortingColumnExtensions,
        FTECheckboxVal,
    } = store;

    const { t } = useTranslation(['officeTable']);
    function translateColumnTitle() {
        columns[0].title = t(columns[0].title);
    }

    useEffect(() => {
        function fetchData() {
            const url = `api/office/internalhours/${columnName}/${direction}/${currentPage}/${pageSize}`;
            cancel && cancel();

            dispatch({ type: 'SET_ISLOADING_TRUE' });

            try {
                axios
                    .get(url, {
                        params: {
                            filters: JSON.stringify(filters),
                        },
                        cancelToken: new CancelToken((c) => {
                            cancel = c;
                        }),
                        headers: {
                            Accept: 'application/json, text/plain, */*',
                            'Content-Type': 'application/json;charset=UTF-8',
                        },
                    })
                    .then((response) => {
                        setTableData(response.data);

                    })
                    .catch((thrown) => {
                        if (axios.isCancel(thrown)) {
                            //TODO Something when request is cancelled?
                        } else {
                            const error = thrown.response;
                            dispatch({ type: 'FETCH_ERROR', payload: error });
                        }
                    });
            } catch (error) {
                console.error(error);
                dispatch({ type: 'SET_ISLOADING_FALSE' });
            }
        }

        if (offices.length > 0 && mainStore.store.loadedTabs['/offices/internalhours']) {
            mainStore.dispatch({ type: 'LOADED_TABS', path: '/offices/internalhours', payload: false });
        } else {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnName, currentPage, filters, pageSize]);

    useEffect(() => {
        HandlePageLoading(isLoading);
        translateColumnTitle();
    }, [isLoading, columns, t]);

    useEffect(() => {
        mainStore.dispatch({ type: 'LOADED_TABS', path: '/offices/internalhours', payload: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setTableData = ({ rows, columns, totalRows, firstInvoiceMonth }) => {
        const formattedRows = FormatRows(rows);
        const formattedColumns = FormatColumns(columns);
        const integratedSortingColumnExtensions = columns.map((col) => ({ columnName: col, compare: SortMonthColumns }));
        PagingShowAllText(t);
        const currentMonthExists = formattedColumns[0].name === moment().format('YYYYMM');
        let monthColumnsToDisplay = window.innerWidth < 900 ? 2 : 5;
        let hiddenColumns;

        if (currentMonthExists) {
            monthColumnsToDisplay += 1;
            hiddenColumns = [...formattedColumns.slice(monthColumnsToDisplay).map((c) => c.name)];
            hiddenColumns.push(formattedColumns[0].name);
        } else {
            hiddenColumns = [...formattedColumns.slice(monthColumnsToDisplay).map((c) => c.name)];
        }

        const newFTEValList = calculateFTEVals(rows);

        dispatch({
            type: 'FETCH_SUCCESS',
            columns: formattedColumns,
            offices: formattedRows,
            columnOrder: [...new Set(formattedColumns.map((columns) => columns.name))],
            hiddenColumns,
            totalRows,
            firstInvoiceMonth,
            integratedSortingColumnExtensions,
            FTEValList: newFTEValList,
        });
    };

    const calculateFTEVals = (rows) => {
        const FTEVals = {};

        rows.map((row) => {
            const len = row.length;
            for (var i = 0; i < len; i++) {
                const idString = row[i].officeId + row[i].month;
                const valPerFTE = parseFloat((row[i].amount / row[i].totalConsultants).toFixed(1));

                Object.assign(FTEVals, { [idString]: { FTEVal: valPerFTE, OrigVal: row[i].amount } });
            }

            return null;
        });

        return FTEVals;
    };

    const handleDateChange = (date) => {
        const { startDate, endDate } = date;

        if (columns.length > 1) {
            const columnNames = [...new Set(columns.map((columns) => columns.name))];

            const visibleColumns = columnNames.filter(
                (columnName) => columnName === 'name' || (parseInt(columnName, 10) >= startDate && parseInt(columnName, 10) <= endDate)
            );

            const hiddenColumns = columnNames.filter((col) => !visibleColumns.includes(col));
            dispatch({ type: 'HIDDEN_COLUMNS', payload: hiddenColumns });
        }
    };

    const handleCheckBoxChange = () => {
        dispatch({ type: 'UPDATE_CHECKBOX', payload: !FTECheckboxVal });
    };

    const handlePageSizeChange = (pageSize) => ChangeTablePage(pageSize, dispatch, currentPage, tableSize);

    return (
        <Grid columns={columns} rows={offices} className='padding-right-60px'>
            <PagingState
                currentPage={currentPage - 1}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                onCurrentPageChange={(currentPage) => {
                    dispatch({ type: 'CURRENT_PAGE', payload: currentPage + 1 });
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }}
            />
            <SortingState defaultSorting={[{ columnName, direction }]} />
            <CustomPaging totalCount={tableSize} />
            <IntegratedSorting columnExtensions={integratedSortingColumnExtensions} />
            <SelectionState />

            <Table
                columnExtensions={TableColumnExtensions}
                cellComponent={(props) => OfficeHoursTableCell(props)}
                rowComponent={(props) => TableRow(props, 'office')}
                noDataRowComponent={(props) => NoDataRow(isLoading, error, props)}
            />

            <TableColumnReordering order={columnOrder} />

            <TableHeaderRow showSortingControls cellComponent={TableHeaderCell} />
            <PagingPanel pageSizes={[10, 25, 50, 10000]} messages={PagingShowAllText} />
            
            <TableColumnVisibility hiddenColumnNames={hiddenColumns} />
            <OfficeHoursTableHeader tableSize={tableSize}>
                <DatePickers firstInvoiceMonth={firstInvoiceMonth} onChange={(date) => handleDateChange(date)} />
                {isLoading ? null : (
                    <ConvertTableToExcel filters={FTECheckboxVal} columns={columns} hiddenColumns={hiddenColumns} table='office' />
                )}
                <FTECheckbox
                    value={FTECheckboxVal}
                    onChange={() => {
                        handleCheckBoxChange();
                    }}
                />
            </OfficeHoursTableHeader>

            {isLoading ? <Loading /> : null}

            <TableFooter>
                <NumberOfSearchResults pageSize={pageSize} tableSize={tableSize} currentPage={currentPage} t={t} />
            </TableFooter>
        </Grid>
    );
};

const TableHeaderCell = ({ ...restProps }) => {
    let classes;
    if (restProps.column.name === 'name') {
        classes = ' padding-left-10px';
    } else {
        classes = ' month-header-cell';
    }
    return <TableHeaderRow.Cell {...restProps} className={classes} />;
};

const FormatRows = (rows) => {
    const formattedRows = rows.map((row) => {
        return row.map((rowDetail, index) => {
            return index === 0
                ? { name: rowDetail.officeName, id: rowDetail.officeId, totalConsultants: rowDetail.totalConsultants }
                : { [rowDetail.month]: rowDetail.amount };
        });
    });

    const tableRows = formattedRows.map((row) => {
        return row.reduce((result, current) => {
            return Object.assign(result, current);
        });
    });

    return tableRows;
};

const FormatColumns = (data) => {
    const columns = data.map((col) => ({ name: col, title: col.slice(0, 4) + '-' + col.slice(4) }));
    data.forEach((element) => {
        TableColumnExtensions.push({ columnName: element, width: 230 });
    });

    const sortedCols = [
        ...columns.sort(function (a, b) {
            if (a.name < b.name) return 1;
            else if (a.name > b.name) return -1;
            else return 0;
        }),
    ];
    return sortedCols;
};

const SortMonthColumns = (a, b) => {
    if (!a) a = 0;
    if (!b) b = 0;

    if (a === b) {
        return 0;
    }
    return a < b ? -1 : 1;
};

export default OfficeHoursTable;

const TableColumnExtensions = [{ columnName: 'name', width: 230 }];
